import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Tilt } from 'react-tilt';
import { FaLinkedin } from 'react-icons/fa';
import { useTheme } from '../context/ThemeContext';

/* ===================== Helpers ===================== */
function initialsAvatar(name = 'User') {
  const parts = name.trim().split(/\s+/);
  const initials = ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
  const svg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='256' height='256'>
      <defs>
        <linearGradient id='g' x1='0' x2='1' y1='0' y2='1'>
          <stop offset='0%' stop-color='#1FA550'/>
          <stop offset='100%' stop-color='#06B6D4'/>
        </linearGradient>
      </defs>
      <rect width='100%' height='100%' rx='128' fill='url(#g)'/>
      <text x='50%' y='54%' dominant-baseline='middle' text-anchor='middle'
            font-family='Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial'
            font-size='96' fill='white' font-weight='800'>${initials}</text>
    </svg>`
  );
  return `data:image/svg+xml;charset=UTF-8,${svg}`;
}

function AvatarImage({ name, srcs = [], className = '' }) {
  const sources = [...srcs, initialsAvatar(name)];
  const [idx, setIdx] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const src = sources[Math.min(idx, sources.length - 1)];
  return (
    <div className={`relative ${className}`}>
      {!loaded && (
        <div
          className="absolute inset-0 rounded-full animate-pulse"
          style={{ background:'linear-gradient(90deg, rgba(2,6,23,.06), rgba(2,6,23,.12), rgba(2,6,23,.06))' }}
        />
      )}
      <img
        src={src}
        alt={name}
        className={`h-full w-full object-cover rounded-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={() => setLoaded(true)}
        onError={() => setIdx((i) => i + 1)}
      />
    </div>
  );
}

/* ===================== Card ===================== */
function TeamCard({ member, tokens }) {
  const [flipped, setFlipped] = useState(false);
  const link = member.linkedin?.startsWith('http') ? member.linkedin : `https://${member.linkedin || ''}`;
  const email = member.email || 'hello@monkeytech.com';
  const tags = member.tags || [];

  const variants = {
    hidden: { opacity: 0, y: 22, scale: 0.985 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55, ease: 'easeOut' } },
    hover: { scale: 1.015 },
  };

  const srcs = [
    member.image,
    member.image?.replace('Kaushal', 'kaushal'),
    member.image?.replace('Vishal', 'vishal'),
    member.image?.replace('.jpg', '.png'),
    member.image?.replace('.png', '.jpg'),
  ].filter(Boolean);

  return (
    <Tilt options={{ max: 16, scale: 1.02, speed: 450, glare: true, 'max-glare': 0.15 }}>
      <motion.div
        className="group relative rounded-[1.35rem] p-[2px] cursor-pointer"
        onClick={() => setFlipped(v => !v)}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setFlipped(v => !v)}
        role="button"
        tabIndex={0}
        title="Flip"
        variants={variants}
        initial="hidden"
        whileInView="show"
        whileHover="hover"
        viewport={{ once: true, amount: 0.4 }}
        style={{
          background: 'linear-gradient(135deg, rgba(31,165,80,0.70), rgba(6,182,212,0.70))',
          boxShadow: tokens.shadow,
        }}
      >
        {/* glass panel */}
        <div
          className="relative overflow-hidden rounded-[1.25rem]"
          style={{ background: tokens.surface, border: `1px solid ${tokens.line}` }}
        >
          {/* hover glow */}
          <div
            className="pointer-events-none absolute -inset-px rounded-[1.25rem] opacity-0 group-hover:opacity-100 transition"
            style={{
              background:
                'radial-gradient(60% 60% at 30% 10%, rgba(31,165,80,0.28), transparent 60%), radial-gradient(60% 60% at 70% 90%, rgba(6,182,212,0.26), transparent 60%)',
              filter: 'blur(26px)',
            }}
          />

          {/* 3D container */}
          <div className="relative h-full min-h-[600px] perspective-1000">
            <div className={`preserve-3d transition-transform duration-700 ease-out ${flipped ? 'rotate-y-180' : ''}`}>
              {/* FRONT */}
              <div className="backface-hidden absolute inset-0 p-8 sm:p-10 flex flex-col items-center text-center">
                <div className="relative mt-2">
                  <div className="absolute -inset-1 rounded-full blur-2xl opacity-70"
                       style={{ background: 'radial-gradient(closest-side, rgba(31,165,80,0.28), transparent)' }} />
                  <div className="relative">
                    <AvatarImage name={member.name} srcs={srcs} className="h-64 w-64 rounded-full" />
                    <div
                      className="pointer-events-none absolute inset-[-8px] rounded-full border border-white/10"
                      style={{ animation: 'spin 18s linear infinite', borderImage: 'linear-gradient(90deg, rgba(31,165,80,.6), rgba(6,182,212,.6)) 1' }}
                    />
                  </div>
                </div>

                <h3 className="mt-6 text-3xl font-bold tracking-tight">{member.name}</h3>
                <span
                  className="mt-2 inline-flex items-center rounded-full px-4 py-1.5 text-base font-semibold"
                  style={{ border: `1px solid ${tokens.line}`, background: tokens.wash }}
                >
                  {member.role}
                </span>

                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 text-base font-semibold text-black"
                  style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Visit ${member.name} LinkedIn`}
                >
                  <FaLinkedin /> LinkedIn
                </a>

                <p className="mt-5 text-lg leading-relaxed text-center" style={{ color: tokens.muted }}>
                  Tap / click to flip the card →
                </p>
              </div>

              {/* BACK */}
              <div className="backface-hidden absolute inset-0 rotate-y-180 p-8 sm:p-10 flex flex-col justify-between">
                <div className="pointer-events-none absolute inset-0 -z-10">
                  <div className="absolute inset-0"
                       style={{ background: 'linear-gradient(135deg, rgba(31,165,80,.10), rgba(6,182,212,.10))' }} />
                  {srcs[0] && (
                    <img
                      src={srcs[0]}
                      alt=""
                      aria-hidden
                      className="absolute inset-0 w-full h-full object-cover opacity-[0.12] blur-[0.5px] scale-110
                                 [mask-image:radial-gradient(circle at 50% 40%, black 55%, transparent 80%)]"
                    />
                  )}
                  <div
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                      backgroundImage: `linear-gradient(${tokens.grid} 1px, transparent 1px), linear-gradient(90deg, ${tokens.grid} 1px, transparent 1px)`,
                      backgroundSize: '44px 44px',
                    }}
                  />
                </div>

                <div className="text-center">
                  <h4 className="text-2xl font-semibold">{member.name}</h4>
                  <p className="mt-4 text-lg leading-relaxed" style={{ color: tokens.muted }}>
                    {member.detail}
                  </p>

                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full px-3 py-1 text-xs sm:text-sm font-semibold"
                        style={{ border: `1px solid ${tokens.line}`, background: tokens.wash }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[
                    { k: 'Projects', v: '10+' },   // ✅ updated
                    { k: 'Since', v: '2025' },
                    { k: 'CSAT', v: '4.9/5' },
                  ].map((s) => (
                    <div
                      key={s.k}
                      className="text-center rounded-xl p-3 backdrop-blur"
                      style={{ border: `1px solid ${tokens.line}`, background: tokens.wash }}
                    >
                      <div className="text-xl font-extrabold">{s.v}</div>
                      <div className="text-xs" style={{ color: tokens.muted }}>{s.k}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-base font-semibold text-black"
                    style={{ background: 'linear-gradient(135deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaLinkedin /> Connect
                  </a>
                  <a
                    href={`mailto:${email}`}
                    className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-base font-semibold"
                    style={{ border: `1px solid ${tokens.line}`, background: tokens.wash, color: tokens.text }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    ✉️ Email
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </Tilt>
  );
}

/* ===================== Section ===================== */
export default function TeamSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const tokens = useMemo(
    () =>
      isDark
        ? {
            text: '#FFFFFF',
            surface: 'rgba(14,20,34,0.90)',
            line: 'rgba(255,255,255,0.10)',
            wash: 'rgba(255,255,255,0.05)',
            shadow: '0 40px 90px rgba(0,0,0,0.55)',
            orb1: 'rgba(31,165,80,0.22)',
            orb2: 'rgba(6,182,212,0.18)',
            grid: 'rgba(255,255,255,0.24)',
            muted: 'rgba(255,255,255,0.82)',
          }
        : {
            text: '#0B1220',
            surface: '#FFFFFF',
            line: 'rgba(2,6,23,0.08)',
            wash: 'rgba(2,6,23,0.04)',
            shadow: '0 36px 80px rgba(2,6,23,0.14)',
            orb1: 'rgba(31,165,80,0.14)',
            orb2: 'rgba(6,182,212,0.12)',
            grid: 'rgba(0,0,0,0.08)',
            muted: 'rgba(17,24,39,0.75)',
          },
    [isDark]
  );

  const teamMembers = [
    {
      name: 'Kaushal Tiwari',
      role: 'Founder & CEO • Partner',
      detail:
        'AI agents & automations, growth & social media handling. Clear roadmaps, fast execution, measurable outcomes since 2025.',
      image: '/images/Kaushal.jpg',
      linkedin: 'https://www.linkedin.com/in/kaushal-tiwari-294628227/',
      email: 'kaushal@monkeytech.com',
      tags: ['AI Agents', 'Automation', 'Growth / Social'],
    },
    {
      name: 'Vishal Rathi',
      role: 'Founder & CTO • Partner',
      detail:
        'Website & application development, product content and delivery quality. Robust architecture & AI-first tooling since 2025.',
      image: '/images/Vishal.png',
      linkedin: 'https://www.linkedin.com/in/vishal-rathi-b24904275/',
      email: 'vishal@monkeytech.com',
      tags: ['Web Dev', 'App Dev', 'Content'],
    },
  ];

  return (
    <>
      <section
        className="relative py-20 sm:py-24 lg:py-28 font-inter"
        style={{ color: tokens.text }}
        aria-labelledby="team-heading"
      >
        {/* background layers */}
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background: isDark
                ? 'linear-gradient(135deg, #0B1220, #0B1325, #000)'
                : 'linear-gradient(135deg, #F6FAFF, #EEF3F9, #FFFFFF)',
            }}
          />
          <div
            className="absolute -top-24 -left-24 h-[32rem] w-[32rem] rounded-full blur-3xl"
            style={{ background: `radial-gradient(closest-side, ${tokens.orb1}, transparent)` }}
          />
          <div
            className="absolute bottom-[-8rem] right-[-8rem] h-[30rem] w-[30rem] rounded-full blur-3xl"
            style={{ background: `radial-gradient(closest-side, ${tokens.orb2}, transparent)` }}
          />
          <div
            className="absolute inset-0 opacity-[0.06] [mask-image:linear-gradient(to_bottom,black,transparent_95%)]"
            style={{
              backgroundImage: `linear-gradient(${tokens.grid} 1px, transparent 1px), linear-gradient(90deg, ${tokens.grid} 1px, transparent 1px)`,
              backgroundSize: '48px 48px',
            }}
          />
        </div>

        {/* heading */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <span
              className="inline-flex items-center rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold"
              style={{ border: `1px solid ${tokens.line}`, background: tokens.wash }}
            >
              Founding Partners • Co-Founders
            </span>
            <h2 id="team-heading" className="mt-4 text-5xl sm:text-6xl font-extrabold tracking-tight">
              Meet Our{' '}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(90deg, var(--brand, #1FA550), var(--accent, #06B6D4))' }}
              >
                Team
              </span>
            </h2>
            <p className="mt-4 text-lg sm:text-xl max-w-3xl mx-auto" style={{ color: tokens.muted }}>
              Two partners. One craft — building reliable, AI-first products that move the needle.
            </p>
          </div>

          {/* cards */}
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
            {teamMembers.map((m) => (
              <TeamCard key={m.name} member={m} tokens={tokens} />
            ))}
          </div>
        </div>
      </section>

      {/* helpers */}
      <style>{`
        .perspective-1000{perspective:1100px}
        .preserve-3d{transform-style:preserve-3d}
        .rotate-y-180{transform:rotateY(180deg)}
        .backface-hidden{backface-visibility:hidden}
        .font-inter{font-family:Inter,ui-sans-serif,system-ui,-apple-system,'Segoe UI',Roboto,Helvetica,Arial}
        :root{--brand:#1FA550;--accent:#06B6D4}
        @keyframes spin{to{transform:rotate(360deg)}}
      `}</style>
    </>
  );
}
